import { supabase } from "@/integrations/supabase/client";

export interface Team {
  id: string;
  owner_profile_id: string;
  domain: string;
  created_at: string;
}

export interface TeamMember {
  team_id: string;
  profile_id: string;
  role: "owner" | "member";
  created_at: string;
  full_name?: string;
  email?: string;
}

/**
 * Get or create a team for the current customer
 * @param profileId - The current user's profile ID
 * @param email - The current user's email (to derive domain)
 * @returns The team object
 */
export async function getOrCreateTeamForCurrentCustomer(
  profileId: string,
  email: string,
): Promise<Team | null> {
  const domain = email.split("@")[1];

  // Try to find existing team where user is owner
  const { data: existingTeam } = await (supabase as any)
    .from("customer_teams")
    .select("*")
    .eq("owner_profile_id", profileId)
    .maybeSingle();

  if (existingTeam) {
    return existingTeam;
  }

  // Create new team
  const { data: newTeam, error } = await (supabase as any)
    .from("customer_teams")
    .insert({
      owner_profile_id: profileId,
      domain,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating team:", error);
    return null;
  }

  // Add owner as a member
  await (supabase as any).from("customer_team_members").insert({
    team_id: newTeam.id,
    profile_id: profileId,
    role: "owner",
  });

  return newTeam;
}

/**
 * List members of a team
 * @param teamId - The team ID
 * @returns Array of team members with profile info
 */
export async function listTeamMembers(teamId: string): Promise<TeamMember[]> {
  const { data, error } = await (supabase as any)
    .from("customer_team_members")
    .select(
      `
      team_id,
      profile_id,
      role,
      created_at,
      profiles (
        full_name,
        email
      )
    `,
    )
    .eq("team_id", teamId);

  if (error) {
    console.error("Error fetching team members:", error);
    return [];
  }

  return (
    data?.map((member: any) => ({
      team_id: member.team_id,
      profile_id: member.profile_id,
      role: member.role,
      created_at: member.created_at,
      full_name: member.profiles?.full_name,
      email: member.profiles?.email,
    })) ?? []
  );
}

/**
 * Invite a member to a team
 * @param teamId - The team ID
 * @param email - The email to invite
 * @param domain - The team domain (for validation)
 * @returns Success status
 */
export async function inviteMember(
  teamId: string,
  email: string,
  domain: string,
): Promise<boolean> {
  // Validate domain matches
  const emailDomain = email.split("@")[1];
  if (emailDomain !== domain) {
    throw new Error(`Email must match team domain (@${domain})`);
  }

  // Find profile by email
  const { data: profile } = await supabase
    .from("profiles")
    .select("id")
    .eq("email", email)
    .maybeSingle();

  if (!profile) {
    // Profile doesn't exist yet - return placeholder message
    // In production, this would send an invite email
    console.log(`Invite created for ${email} (profile not found)`);
    return true;
  }

  // Check if already a member
  const { data: existingMember } = await (supabase as any)
    .from("customer_team_members")
    .select("*")
    .eq("team_id", teamId)
    .eq("profile_id", profile.id)
    .maybeSingle();

  if (existingMember) {
    throw new Error("User is already a member of this team");
  }

  // Add as member
  const { error } = await (supabase as any).from("customer_team_members").insert({
    team_id: teamId,
    profile_id: profile.id,
    role: "member",
  });

  if (error) {
    console.error("Error inviting member:", error);
    return false;
  }

  return true;
}
